import { useState } from 'react'
import { useReleaseLead, useTransferLead } from '@/shared/api/leads'
import { useColleagues } from '@/shared/api/session'
import { fmtDate } from '@/shared/lib/format'
import { cls } from '@/ui/cls'
import { Ico } from '@/ui/Ico'
import { initials } from '@/shared/lib/contact'
import { useBrand } from '@/shared/white-label/useBrand'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@/components/ui/popover'

interface Props {
  leadId: string
  /** Owner's email, or null when the lead is in the shared pool. */
  ownerEmail: string | null
  /** When ownership started — "Yours since 12 Aug". */
  ownerSince: string | null
  /** True when the signed-in user is the owner. */
  mine: boolean
  /**
   * Whether this user may change ownership at all. Decided by the caller, which
   * is the only place that knows about read-only mode — a non-seller admin who
   * happens to own a lead must still be held by that guard.
   */
  canMove: boolean
  /** Releasing a colleague's lead requires user administration, not team transfer. */
  canRelease: boolean
  /**
   * How many times this lead has been worked — calls, notes, emails, meetings.
   * Anything above zero turns "Return to the pool" into a two-step action: the
   * work is the thing a colleague cannot see before they dial, so putting a
   * called lead back is a decision, not a click.
   */
  workedCount: number
}

/**
 * Who has this lead, and the ways it can change hands.
 *
 * Replaces a row of four same-weight chips — `Yours` `Release` `[picker]`
 * `Give to Erik` — where a status label, a destructive action, a form field and
 * a primary button were visually indistinguishable, and the picker read as a
 * filter because its only label was a placeholder that vanished once you chose.
 *
 * One control now. It states the fact (who owns it) and opens to the moves
 * (hand it over, put it back). Ownership is a single subject, so it gets a
 * single affordance rather than being spread across the strip.
 *
 * A row click commits. The earlier two-step guard existed because a bare select
 * fires on arrow keys, so a keypress could move a live lead; opening a menu and
 * clicking a named person is already deliberate, and the row says exactly what
 * will happen.
 */
export default function OwnershipMenu({
  leadId,
  ownerEmail,
  ownerSince,
  mine,
  canMove,
  canRelease,
  workedCount,
}: Props) {
  const [open, setOpen] = useState(false)
  const [confirmingRelease, setConfirmingRelease] = useState(false)
  const { data } = useColleagues()
  const { person, anonymised } = useBrand()
  const transfer = useTransferLead()
  const release = useReleaseLead()

  const owner = ownerEmail?.toLowerCase() ?? null
  const targets = (data?.users ?? []).filter(
    c => c.takesLeads && c.email.toLowerCase() !== owner,
  )
  // Masked here rather than at each render site: this one value feeds the owner
  // chip, its tooltip, the popover header and the button label, and the chip's
  // tooltip was still printing the local-part after every other site was done.
  const ownerName = anonymised
    ? (ownerEmail ? person(ownerEmail) : null)
    : (data?.users.find(u => u.email.toLowerCase() === owner)?.name
       ?? ownerEmail?.split('@')[0]
       ?? null)

  // Which of the offered names cannot stand on their own. Two people whose
  // names read alike — a duplicate account, a married pair of Anderssons, an
  // account whose display name is its own address — are one wrong click apart,
  // and the click moves a live lead. Comparison is deliberately blunter than
  // equality: an address is reduced to its local part, punctuation and case are
  // dropped, and runs of the same letter collapse, so "Willhelm Seidow",
  // "wilhelm.seidow@yllw.se" and "Wilhelm  Seidow" all fold together. Rows that
  // fold onto another row get their address shown; the rest stay a bare name.
  const ambiguous = new Set<string>()
  const seen = new Map<string, string>()
  for (const c of targets) {
    const key = foldName(c.name || c.email)
    const first = seen.get(key)
    if (first === undefined) seen.set(key, c.email)
    else {
      ambiguous.add(first)
      ambiguous.add(c.email)
    }
  }

  const busy = transfer.isPending || release.isPending
  const worked = workedCount > 0
  const close = () => {
    setOpen(false)
    setConfirmingRelease(false)
  }

  // Nothing to offer and nothing to say beyond the label: render the plain fact.
  if (!canMove || (targets.length === 0 && !ownerEmail)) {
    return ownerEmail ? <OwnerChip name={ownerName} mine={mine} /> : null
  }

  const label = ownerEmail ? (mine ? 'Din' : ownerName) : 'Otilldelad'

  return (
    <Popover open={open} onOpenChange={next => { setOpen(next); if (!next) setConfirmingRelease(false) }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={busy}
          aria-label="Ägarskap — överlämna den här leaden eller lägg tillbaka den i poolen"
          className={cls(
            'inline-flex items-center gap-1.5 px-2 py-0.5 border rounded-[2px]',
            'text-[11px] whitespace-nowrap transition-colors cursor-pointer',
            '[&_svg]:w-3 [&_svg]:h-3 disabled:opacity-60',
            open
              ? 'bg-ink-1000 border-ink-1000 text-white'
              : ownerEmail
                ? 'bg-white border-ink-200 text-ink-1000 hover:border-ink-1000'
                : 'bg-ink-50 border-ink-200 text-ink-500 hover:border-ink-1000 hover:text-ink-1000',
          )}
        >
          <span className={cls('font-medium', !ownerEmail && 'font-normal')}>
            {busy ? 'Flyttar…' : label}
          </span>
          {Ico.chevronDown}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" sideOffset={4} className="w-64 rounded-[2px] p-0">
            <div className="px-3 py-2 border-b border-ink-100">
              <PopoverTitle className="text-[10px] font-normal uppercase tracking-widest text-ink-500">
                Ägarskap
              </PopoverTitle>
              {/* Two lines, because "Yours since Today" reads badly and
                  fmtDate returns relative words as often as dates. */}
              <div className="text-[12px] text-ink-1000 mt-0.5">
                {ownerEmail
                  ? (mine ? 'Din' : ownerName)
                  : <span className="text-ink-500">I den delade poolen</span>}
              </div>
              {ownerEmail && ownerSince && (
                <div className="text-[11px] text-ink-500">Sedan {fmtDate(ownerSince)}</div>
              )}
            </div>

            {targets.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-1">
                  <div className="text-[10px] uppercase tracking-widest text-ink-500">
                    {ownerEmail ? 'Överlämna till' : 'Tilldela till'}
                  </div>
                  {worked && (
                    <div className="text-[11px] text-ink-700 mt-0.5">
                      De får samtalshistoriken med sig.
                    </div>
                  )}
                </div>
                {targets.map(c => {
                  const name = c.name || c.email
                  // A display name that is already the address says it once.
                  const showEmail =
                    ambiguous.has(c.email)
                    && name.toLowerCase() !== c.email.toLowerCase()
                  return (
                    <button
                      key={c.email}
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        transfer.mutate(
                          { id: leadId, to: c.email },
                          { onSuccess: close },
                        )
                      }
                      className="w-full text-left px-3 py-1.5 hover:bg-ink-50 cursor-pointer
                                 flex items-center gap-2 disabled:opacity-60"
                    >
                      <Avatar size="xs">
                        <AvatarFallback className="bg-ink-100 text-ink-700">{initials(name)}</AvatarFallback>
                      </Avatar>
                      <span className="min-w-0">
                        <span className="block text-[13px] text-ink-1000 truncate">
                          {anonymised ? person(c.email) : name}
                        </span>
                        {showEmail && !anonymised && (
                          <span className="block text-[11px] text-ink-500 truncate">
                            {c.email}
                          </span>
                        )}
                      </span>
                    </button>
                  )
                })}
              </>
            )}

            {ownerEmail && canRelease && (
              <div className="border-t border-ink-100 mt-1">
                {confirmingRelease ? (
                  <div className="px-3 py-2.5">
                    <div className="text-[12px] text-ink-1000 leading-[1.45]">
                      Du har bearbetat den här leaden {workedCount}{' '}
                      {workedCount === 1 ? 'gång' : 'gånger'}.
                    </div>
                    <div className="text-[11px] text-ink-700 leading-[1.45] mt-1">
                      Lägg tillbaka den så försvinner den från din hög. Vem som helst
                      av dina kollegor kan ta den och ringa samma personer igen. Vill
                      du att den stannar hos någon, överlämna den till ett namn ovan
                      istället.
                    </div>
                    <div className="flex items-center gap-1.5 mt-2.5">
                      <Button
                        type="button"
                        variant="destructive"
                        size="xs"
                        loading={release.isPending}
                        disabled={busy}
                        onClick={() => release.mutate(leadId, { onSuccess: close })}
                      >
                        Lägg tillbaka ändå
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="xs"
                        disabled={busy}
                        onClick={() => setConfirmingRelease(false)}
                      >
                        Behåll
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      worked
                        ? setConfirmingRelease(true)
                        : release.mutate(leadId, { onSuccess: close })
                    }
                    className={cls(
                      'w-full text-left px-3 py-2 hover:bg-ink-50 cursor-pointer',
                      'text-[13px] disabled:opacity-60',
                      worked ? 'text-ink-500' : 'text-ink-700',
                    )}
                    title={
                      worked
                        ? 'Ber om bekräftelse — den här leaden har bearbetats'
                        : 'Vem som helst kan sedan ta den'
                    }
                  >
                    Lägg tillbaka i poolen
                  </button>
                )}
              </div>
            )}

            {(transfer.isError || release.isError) && (
              <div
                className="px-3 py-2 border-t border-ink-100 text-[11px] text-[var(--color-danger)]"
                role="alert"
              >
                Det gick inte igenom. Någon kan ha flyttat den först.
              </div>
            )}
      </PopoverContent>
    </Popover>
  )
}

/**
 * A name reduced to what the eye actually reads: local part of an address,
 * accents flattened, letters only, doubled letters collapsed. Two names with the
 * same fold are not safe to tell apart at a glance.
 */
function foldName(value: string): string {
  return value
    .trim()
    .split('@')[0]
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/(.)\1+/g, '$1')
}

/** Ownership as a read-only fact, for a viewer with no moves available. */
function OwnerChip({ name, mine }: { name: string | null; mine: boolean }) {
  return (
    <Badge
      variant={mine ? 'outline' : 'secondary'}
      className={cls('rounded-[2px] font-normal', mine && 'font-medium text-ink-1000')}
      title={
        mine
          ? 'Tilldelad dig — ingen annan ser den här i sin kö'
          : `${name} har den här leaden. Att logga ett samtal här tar inte bort den från personen.`
      }
    >
      {mine ? 'Din' : name}
    </Badge>
  )
}
